import React, { useState } from 'react';
import { Box, Container, Grid, Typography, Button, Checkbox, FormGroup, FormControlLabel, IconButton, Drawer, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FilterListIcon from '@mui/icons-material/FilterList';
import glowBooster from '../../assets/glow_booster.png';
import energyBoost from '../../assets/energy-boost.png';
import proAge from '../../assets/pro-age.png';
import hyaluMinerals from '../../assets/hyalu_minerals.png';
import bielendaLogo from '../../assets/bielenda_logo.png';
import larcLogo from '../../assets/l\'arc-logo.png';
import { useAppContext } from '../AppContext';

export const Page5: React.FC = () => {
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedBrands, setSelectedBrands] = useState<string[]>(['bielenda']); // Default to Bielenda
    const [selectedSkinTypes, setSelectedSkinTypes] = useState<string[]>([]);
    const [selectedConcerns, setSelectedConcerns] = useState<string[]>([]);
    const { openModal } = useAppContext();

    const handleBrandChange = (brand: string) => {
        setSelectedBrands(prev =>
            prev.includes(brand) ? prev.filter(b => b !== brand) : [...prev, brand]
        );
    };

    const handleSkinTypeChange = (type: string) => {
        setSelectedSkinTypes(prev =>
            prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
        );
    };

    const handleConcernChange = (concern: string) => {
        setSelectedConcerns(prev =>
            prev.includes(concern) ? prev.filter(c => c !== concern) : [...prev, concern]
        );
    };

    const products = [
        // Bielenda Products
        {
            name: 'Bielenda Supremelab Glow Booster Elixir - HYDRA GLOW',
            volume: '30ml',
            price: 'RM 383',
            image: glowBooster,
            brand: 'bielenda',
            skinType: ['Normal', 'Combination', 'Dehydrated', 'Dull', 'Tired'],
            concern: ['Hydrating', 'Soothing', 'Brightening', 'Firming'],
            path: '/product/hydra-glow'
        },
        {
            name: 'Bielenda Supremelab Brightening Serum - ENERGY BOOST',
            volume: '15ml',
            price: 'RM 268',
            image: energyBoost,
            brand: 'bielenda',
            skinType: ['Normal', 'Oily', 'Combination', 'Tired', 'Dull'],
            concern: ['Brightening', 'Anti-aging', 'Antioxidant', 'Reducing Discoloration'],
            path: '/product/energy-boost'
        },
        {
            name: 'Bielenda Supremelab Anti-Wrinkle Serum - PRO AGE EXPERT',
            volume: '30ml',
            price: 'RM 278',
            image: proAge,
            brand: 'bielenda',
            skinType: ['Mature', 'Dry', 'Normal'],
            concern: ['Anti-aging', 'Firming', 'Hydrating', 'Nourishing'],
            path: '/product/pro-age-expert'
        },
        {
            name: 'Bielenda Supremelab Hydro-Serum - HYALU MINERALS',
            volume: '30ml',
            price: 'RM 348',
            image: hyaluMinerals,
            brand: 'bielenda',
            skinType: ['Dehydrated', 'Combination', 'Weakened', 'Dull', 'Tired', 'Problematic'],
            concern: ['Hydrating', 'Smoothing', 'Firming', 'Strengthening'],
            path: '/product/hyalu-minerals'
        },
    ];

    const filteredProducts = products.filter(product => {
        // Since this page is only for Bielenda products, we don't need brand filtering
        const matchesSkinType = selectedSkinTypes.length === 0 || product.skinType.some(type => selectedSkinTypes.includes(type));
        const matchesConcern = selectedConcerns.length === 0 || product.concern.some(c => selectedConcerns.includes(c));
        return matchesSkinType && matchesConcern;
    });

    const FilterContent = () => (
        <Box sx={{ padding: '20px', backgroundColor: '#fff', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
            <Typography
                sx={{
                    fontFamily: 'Quicksand',
                    fontSize: '24px',
                    fontWeight: 600,
                    lineHeight: '36px',
                    color: 'rgba(61, 61, 61, 1)',
                    marginTop: '20px',
                    marginBottom: '10px',
                    textAlign: 'left',
                }}
            >
                Brand
            </Typography>
            <Box sx={{
                padding: '10px 0',
                borderBottom: '1px solid rgba(221, 221, 221, 1)',
                marginBottom: '10px'
            }}>
                <Typography
                    sx={{
                        fontFamily: 'Quicksand',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'rgba(0, 48, 135, 1)',
                        textTransform: 'uppercase',
                    }}
                >
                    BIELENDA SUPREMELAB
                </Typography>
            </Box>

            <Typography
                sx={{
                    fontFamily: 'Quicksand',
                    fontSize: '24px',
                    fontWeight: 600,
                    lineHeight: '36px',
                    color: 'rgba(61, 61, 61, 1)',
                    marginTop: '30px',
                    marginBottom: '10px',
                    textAlign: 'left',
                }}
            >
                Skin Type
            </Typography>
            <FormGroup>
                {['Normal', 'Dry', 'Oily', 'Combination', 'Dehydrated', 'Dull', 'Tired', 'Problematic', 'Weakened', 'Mature', 'Sensitive'].map((type) => (
                    <FormControlLabel
                        key={type}
                        control={
                            <Checkbox
                                checked={selectedSkinTypes.includes(type)}
                                onChange={() => handleSkinTypeChange(type)}
                                sx={{
                                    color: 'rgba(0, 48, 135, 1)',
                                    '&.Mui-checked': { color: 'rgba(0, 48, 135, 1)' },
                                }}
                            />}
                        label={type.toUpperCase()}
                        sx={{
                            textAlign: 'left', textTransform: 'uppercase',
                            fontFamily: 'Quicksand',
                            fontSize: '14px',
                            fontWeight: 200,
                            color: 'rgba(61, 61, 61, 1)',
                        }}
                    />
                ))}
            </FormGroup>
            <Typography
                sx={{
                    fontFamily: 'Quicksand',
                    fontSize: '24px',
                    fontWeight: 600,
                    lineHeight: '36px',
                    color: 'rgba(61, 61, 61, 1)',
                    marginTop: '30px',
                    marginBottom: '10px',
                    textAlign: 'left',
                }}
            >
                Skin Concerns
            </Typography>
            <FormGroup>
                {['Hydrating', 'Soothing', 'Brightening', 'Anti-aging', 'Firming', 'Smoothing', 'Strengthening', 'Reducing Discoloration', 'Antioxidant', 'Nourishing', 'Cleansing', 'Gentle'].map((concern) => (
                    <FormControlLabel
                        key={concern}
                        control={
                            <Checkbox
                                checked={selectedConcerns.includes(concern)}
                                onChange={() => handleConcernChange(concern)}
                                sx={{
                                    color: 'rgba(0, 48, 135, 1)',
                                    '&.Mui-checked': { color: 'rgba(0, 48, 135, 1)' },
                                }}
                            />}
                        label={concern.toUpperCase()}
                        sx={{
                            textAlign: 'left', textTransform: 'uppercase',
                            fontFamily: 'Quicksand',
                            fontSize: '14px',
                            fontWeight: 200,
                            color: 'rgba(61, 61, 61, 1)',
                        }}
                    />
                ))}
            </FormGroup>
            {(selectedSkinTypes.length > 0 || selectedConcerns.length > 0) && (
                <Button
                    variant="outlined"
                    onClick={() => {
                        setSelectedSkinTypes([]);
                        setSelectedConcerns([]);
                    }}
                    sx={{
                        marginTop: '20px',
                        width: '100%',
                        borderColor: 'rgba(0, 48, 135, 1)',
                        color: 'rgba(0, 48, 135, 1)',
                        borderRadius: '50px',
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        fontFamily: 'Quicksand',
                        fontSize: '14px',
                        '&:hover': {
                            borderColor: 'rgba(0, 48, 135, 1)',
                            backgroundColor: 'rgba(0, 48, 135, 0.05)',
                        },
                    }}
                >
                    Clear All Filters
                </Button>
            )}
        </Box>
    );

    return (
        <Container maxWidth={false} sx={{ padding: '40px 20px', backgroundColor: '#fff' }} id={'products'}>
            <Grid container spacing={4}>
                {/* Left Filters for Desktop */}
                <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: '20px', height: 'fit-content' }}>
                    <FilterContent />
                </Grid>

                {/* Product Grid */}
                <Grid item xs={12} md={9}>
                    {/* Filter Button for Mobile */}
                    <Stack flexDirection='row'>
                        <IconButton
                            sx={{ display: { xs: 'block', md: 'none' }, marginBottom: '20px', }}
                            onClick={() => setDrawerOpen(true)}
                        >
                            <FilterListIcon sx={{ fontSize: '32px' }} />
                        </IconButton>
                    </Stack>

                    <Drawer
                        anchor="left"
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                        sx={{ '& .MuiDrawer-paper': { width: '80%', padding: '20px' } }}
                    >
                        <FilterContent />
                    </Drawer>

                    {/* Bielenda Products Grid */}
                    <Grid container spacing={4}>
                        {filteredProducts.map((product, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Box
                                    sx={{
                                        borderRadius: '36px',
                                        border: '2px solid rgba(0, 48, 135, 0.3)',
                                        overflow: 'hidden',
                                        padding: '20px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                        backgroundColor: 'rgba(0, 48, 135, 0.02)',
                                        position: 'relative',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: '0 8px 25px rgba(0, 48, 135, 0.2)',
                                        },
                                    }}
                                    onClick={() => navigate(product.path)}
                                >
                                    {/* Brand Badge */}
                                    <Box sx={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        backgroundColor: 'rgba(0, 48, 135, 0.9)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        fontFamily: 'Quicksand',
                                        textTransform: 'uppercase',
                                        zIndex: 1,
                                    }}>
                                        Bielenda
                                    </Box>

                                    <Box
                                        component="img"
                                        src={product.image}
                                        alt={product.name}
                                        sx={{
                                            width: '100%',
                                            maxHeight: '200px',
                                            height: '200px',
                                            borderRadius: '24px',
                                            objectFit: 'contain',
                                            marginBottom: '10px',
                                            marginTop: '20px',
                                        }}
                                    />
                                    <Box sx={{ textAlign: 'left', flexGrow: 1, position: 'relative' }}>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Quicksand',
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                color: 'rgba(0, 48, 135, 1)',
                                                marginBottom: '5px',
                                                textTransform: 'uppercase',
                                            }}
                                        >
                                            Bielenda Supremelab
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Quicksand',
                                                fontSize: '18px',
                                                fontWeight: 600,
                                                color: 'rgba(61, 61, 61, 1)',
                                                marginBottom: '5px',
                                            }}
                                        >
                                            {product.name}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Quicksand',
                                                fontSize: '13px',
                                                fontWeight: 400,
                                                color: 'rgba(136, 136, 136, 1)',
                                                marginBottom: '10px',
                                            }}
                                        >
                                            {product.volume}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Quicksand',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                color: 'rgba(61, 61, 61, 1)',
                                                marginBottom: '20px',
                                            }}
                                        >
                                            {product.price}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            borderColor: 'rgba(0, 48, 135, 1)',
                                            color: 'rgba(0, 48, 135, 1)',
                                            borderRadius: '50px',
                                            textTransform: 'uppercase',
                                            fontWeight: 600,
                                            width: '100%',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                backgroundColor: 'rgba(0, 48, 135, 1)',
                                                zIndex: 0,
                                                transform: 'translateX(-100%)',
                                                transition: 'transform 0.4s ease',
                                            },
                                            '&:hover::before': {
                                                transform: 'translateX(0)',
                                            },
                                            '&:hover': {
                                                borderColor: '#003365',
                                                color: '#fff',
                                            },
                                            '& .button-text': {
                                                position: 'relative',
                                                zIndex: 1,
                                            },
                                        }}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            openModal();
                                        }}
                                    >
                                        <span className="button-text">Buy Now</span>
                                    </Button>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    );
};

export default Page5;